import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { KeyRound, PauseCircle, PlayCircle } from 'lucide-react';
import { DeskTable, ElementTable, ElementCell } from '../../components/nucleus/Shelf';
import NucleusPage, { Verb } from '../../components/nucleus/NucleusPage';
import { useFacets, Faceted } from '../../components/nucleus/FacetRail';
import { Cell } from '../../components/nucleus/Collection';
import { Empty } from '../../components/nucleus/Empty';
import { Note, useOpen, useLinkedItem } from '../../components/nucleus/Inspector';
import { useRunAction } from '../../components/nucleus/actions';
import { elementOf } from '../../components/nucleus/cells';
import { useToast } from '../../components/common';
import api from '../../services/api';
import { usePlace } from '../../nucleus/place';
import { spaceWords } from '../../nucleus/access';
import { kindOne } from '../nucleus/RealmsModel';
import { GrantMark, appOf, badgeOf } from '../nucleus/RealmsParts';
import StatusPill from '../../components/nucleus/StatusPill';
import { useRealmsData, realmsApi } from '../realms/realmsData';
import GrantInspector, { grantActionsOf, useGrantHrefs } from './GrantInspector';
import TokenMaker from './TokenMaker';
import { PAGE_KIND, KIND_WORD, KIND_ORDER, connectionsOf, findConnection, lapsingSoon, statusOf, statusText, STATUS_WORD } from './accessData';
import { isStanding, standingOff, useStandingAutomations, withStanding } from './standing';
import '../realms/realms.css';

// EXPOSURE PROFILES — one per app, person or group you have shared with: what it
// gets, from which privacy realms, how you appear, until when (the consent page's
// grant). Each is a contract, sealed with its holder's face; the list filters by
// status (Active, Paused, Expired, Revoked, as the consent page names them), who,
// what needs a look, which realm. A link can land on a filter (?status=inactive&kind=apps).
const KIND_HUE = { group: '#5b68a0', delegate: '#7a5aa6', cosm_agent: '#4f7d8c', token: 'var(--nx-zone-gates)' };

// one connection's status: its current exposure profile's
const statusOfC = (c) => statusOf(c.first);
const lapsing = (c) => c.live.some((p) => lapsingSoon(p));
const wide = (c) => c.live.some((p) => p.unbounded);
const LIVE = ['active', 'paused', 'waiting'];
const ENDED = ['expired', 'revoked'];
const LOOK_WORD = { lapsing: 'Expires within 14 days', wide: 'Gets all your data' };
const facetsOf = (w) => [
    { id: 'status', title: 'Status', of: statusOfC, order: [...LIVE, ...ENDED], always: true, label: (k) => STATUS_WORD[k] },
    { id: 'kind', title: 'Who', of: (c) => c.kind, order: KIND_ORDER, label: (k) => KIND_WORD[k] || k },
    { id: 'state', title: 'Needs a look', of: (c) => [lapsing(c) && 'lapsing', wide(c) && 'wide'].filter(Boolean), order: ['lapsing', 'wide'], label: (k) => (k === 'wide' ? `Gets all ${w.inGroup ? `of ${w.your}` : 'your'} data` : LOOK_WORD[k]) },
    { id: 'realm', title: 'Shared from', of: (c) => [...new Set((c.live.length ? c.live : c.ended).flatMap((p) => (p.realms.length ? p.realms.map((r) => r.name) : [p.realmName])).filter(Boolean))] },
];
// pause all / resume all reach the contracts with apps, agents and people, never
// a group's own members' access (the server leaves group_member grants alone), a
// schedule's or automation's, or a plugin run's (NOT_PAUSE_ALL_TYPES: stored paused,
// and never turned on by Resume all)
const NOT_PAUSE_ALL = new Set(['group_member', 'automation', 'plugin_execution', 'plugin_delegation']);
const pausable = (p) => !NOT_PAUSE_ALL.has(p.entityType);
// a list's heading: whose, and with whom; a schedule's or an automation's contract
// is filed under what it is
const listTitle = (k, w) => (k === 'automation' ? `${w.Your} automations` : k === 'token' ? `${w.Your} tokens`
    : `${w.Your} contracts with ${KIND_WORD[k].toLowerCase()}`);
const textOf = (c) => c.all.map((p) => [p.label, p.realmName, ...p.realms.map((r) => r.name)].join(' ')).join(' ');

// `data` is the section's read (RealmsSection), shared with the realms page so that
// switching between the two reads nothing again; on its own, the page reads it itself.
export default function AccessSection({ data }) {
    return data ? <AccessPage data={data} /> : <OwnRead />;
}

function OwnRead() {
    const { groupId, group } = usePlace();
    return <AccessPage data={useRealmsData(groupId, group)} />;
}

function AccessPage({ data }) {
    const { section, page, groupId, isAdmin, spaceName, to, navigate } = usePlace();
    const calls = useMemo(() => realmsApi(groupId), [groupId]);
    const hrefs = useGrantHrefs();
    const toast = useToast();
    const run = useRunAction();
    const [openId, open, close] = useOpen();
    const [params, setParams] = useSearchParams();
    // the token sheet: null, or what it starts from ({ realmIds, name })
    const [making, setMaking] = useState(null);
    // where a link lands: the live ones (active and paused) unless it asks for the
    // ended ones, and any kind or need it names
    const [initial] = useState(() => {
        const st = params.get('status');
        return {
            status: st === 'inactive' ? ENDED : st === 'paused' || params.get('state') === 'paused' ? ['paused'] : LIVE,
            kind: PAGE_KIND[params.get('kind')] ? [PAGE_KIND[params.get('kind')]] : [],
            state: LOOK_WORD[params.get('state')] ? [params.get('state')] : [],
        };
    });

    const w = spaceWords(groupId, spaceName);
    const facets = useMemo(() => facetsOf(w), [groupId, spaceName]); // eslint-disable-line react-hooks/exhaustive-deps
    // A group's contracts with its members (its side of each membership: what each can
    // do in it) are listed too, in a section of their own; the realms page leaves them
    // out of what a realm is shared into.
    // a schedule's or an automation's contract stands as its automation does: read
    // the space's automations once one is listed, and name each by what it runs
    const autos = useStandingAutomations(groupId, data.profiles.some(isStanding));
    const profiles = useMemo(() => withStanding(data.profiles, autos), [data.profiles, autos]);
    const connections = useMemo(() => connectionsOf([...profiles, ...(data.memberGrants || [])]), [profiles, data.memberGrants]);
    const active = connections.filter((c) => c.live.length > 0);
    const f = useFacets(connections, facets, { text: textOf, placeholder: 'Filter contracts', initial });
    const shown = f.shown;
    const current = findConnection(connections, openId);
    // a link that names one grant by its id opens that grant, not the one in use
    const openGrant = current?.all.find((x) => String(x.id) === String(openId))?.id || null;
    // A contract a link names that the list does not hold. The list is every
    // grant of the space, revoked ones too, so one missing is either newer than
    // the list (read it on its own, then the list again) or not this space's.
    // A group has no one-contract route, and a kind:id key names no grant id:
    // those go straight to saying so.
    const linked = useLinkedItem(openId, {
        ready: data.loaded,
        inList: !!current,
        read: async (id) => {
            if (groupId || String(id).includes(':')) return null;
            const j = await api.request(`/exposure-profiles/${encodeURIComponent(id)}`)
                .catch((e) => { if (e?.status === 404 || e?.status === 403) return null; throw e; });
            if (!j?.id) return null;
            data.reloadGrants();
            return j;
        },
    });

    // ?new=token&realm=<id> opens the token sheet on that realm: a realm's "Make
    // token" comes here. Read once, then taken off the address so Back does not reopen it.
    const asked = useRef(false);
    useEffect(() => {
        if (asked.current || !data.loaded || params.get('new') !== 'token') return;
        asked.current = true;
        if (isAdmin) setMaking({ realmIds: params.get('realm') ? [params.get('realm')] : [] });
        setParams((q) => { const n = new URLSearchParams(q); n.delete('new'); n.delete('realm'); return n; }, { replace: true });
    }, [data.loaded, params, setParams, isAdmin]);
    const tokenHint = isAdmin ? 'A token for a script or tool you run' : `Only an admin of ${spaceName} makes tokens in it`;

    const ctx = {
        isAdmin, spaceName, calls, toast, reload: data.reloadGrants, hrefs, to,
        newToken: (preset) => setMaking(preset || {}),
        openHref: (href) => navigate(href),
        openMember: (p) => navigate(to('people', null, `open=${encodeURIComponent(p.entityId)}`)),
    };
    const actionsFor = (p) => grantActionsOf(p, ctx);

    const cell = (c) => {
        const p = c.first;
        const gone = !c.live.length;
        const s = p.standing;
        const badge = gone
            ? <StatusPill status={c.ended.some((x) => x.revoked) ? 'revoked' : 'expired'} of="contract" />
            : s ? (s.known ? <StatusPill status={s.state} of={s.of} title={s.why || undefined} check={false} /> : undefined)
                : statusOf(p) === 'waiting'
                    ? <StatusPill status="waiting" of="contract" title={statusText(p)} label={statusText(p)} />
                    : badgeOf(c.live);
        const dim = gone || (s ? standingOff(s) || s.gone : c.live.every((x) => !x.active));
        const note = (gone ? c.ended : c.live).map((x) => x.realms.map((r) => r.name).join(' + ') || x.realmName).join(' · ');
        const props = {
            note,
            badge,
            current: current?.key === c.key,
            onPress: () => open(c.key),
            className: dim ? 'realms-paused' : undefined,
            title: p.label,
        };
        return (
            <Cell key={c.key} actions={actionsFor(p)}>
                <ElementCell
                    tag={p.kind === 'app' || p.kind === 'member' ? undefined : s?.schedule ? undefined : kindOne(p.kind).toUpperCase()}
                    hue={p.kind === 'app' ? elementOf(appOf(p))?.hue : KIND_HUE[p.kind]}
                    mark={<GrantMark profile={p} size={50} />}
                    name={p.label}
                    {...props}
                />
            </Cell>
        );
    };

    // pause or resume every live grant at once (what Health held)
    const live = (data.live || []).filter(pausable);
    const runningN = live.filter((p) => p.active).length;
    const pausedN = live.filter((p) => !p.active && !p.waiting).length;
    const hint = isAdmin ? undefined : `Only an admin of ${spaceName} can pause or resume its contracts`;
    const contractsN = (n) => (n === 1 ? 'The one contract' : `All ${n} contracts`);
    const everyone = [
        {
            id: 'pause-all', label: 'Pause all', icon: <PauseCircle />, disabled: !isAdmin || !runningN, hint,
            confirm: groupId ? {
                title: `Pause every contract in ${spaceName}?`,
                body: `${contractsN(runningN)} with apps, agents and people ${runningN === 1 ? 'pauses' : 'pause'}: nothing of ${spaceName}’s passes to them until you resume. Members keep their access. Tools that run automatically, and automations, keep running. Nothing is revoked.`,
                label: 'Pause all',
            } : {
                title: 'Pause every contract?',
                body: `${contractsN(runningN)} with apps, agents and people ${runningN === 1 ? 'pauses' : 'pause'}: nothing passes to them until you resume. Tools that run automatically, and automations, keep running. Nothing is revoked.`,
                label: 'Pause all',
            },
            run: async () => {
                await calls.pauseAll();
                toast.success(groupId ? 'Every contract with an app, agent or person is paused. Members keep their access.' : 'Every contract is paused. Nothing passes.');
                data.reloadGrants();
            },
        },
        {
            id: 'resume-all', label: 'Resume all', icon: <PlayCircle />, disabled: !isAdmin || !pausedN, hint,
            confirm: {
                title: groupId ? `Resume every contract in ${spaceName}?` : 'Resume every contract?',
                body: 'Every contract that Pause all paused runs again, exactly as it was before. One you paused on its own stays paused.',
                label: 'Resume all',
            },
            run: async () => { await calls.resumeAll(); toast.success('Every contract is running again.'); data.reloadGrants(); },
        },
    ];

    // in a group: apps, the public page, then its members, then the groups it is connected to
    const order = groupId ? [...KIND_ORDER.filter((k) => k !== 'member' && k !== 'group'), 'member', 'group'] : KIND_ORDER;
    const sets = order.map((k) => ({ k, list: shown.filter((c) => c.kind === k) })).filter((s) => s.list.length);
    const empty = !connections.length ? `${w.youHavent} shared with anything yet.` : 'Nothing matches the filters.';

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={data.loaded ? active.length : undefined}
            verbs={<>
                {everyone.map((a) => (
                    <Verb key={a.id} icon={a.icon} disabled={a.disabled} title={a.hint || a.label} onClick={() => run(a)}>{a.label}</Verb>
                ))}
                <Verb icon={<KeyRound />} primary disabled={!isAdmin} title={tokenHint} onClick={() => setMaking({})}>New token</Verb>
            </>}
            inspector={current && (
                <GrantInspector
                    key={`${current.key}|${openGrant || ''}`}
                    connection={current}
                    grantId={openGrant}
                    data={data}
                    calls={calls}
                    actionsFor={actionsFor}
                    onClose={close}
                />
            )}
        >
            {making && (
                <TokenMaker
                    realms={data.realms}
                    schemaNames={data.schemaNames}
                    preset={making}
                    groupId={groupId}
                    spaceName={spaceName}
                    calls={calls}
                    onDone={(made) => { setMaking(null); if (made) data.reloadGrants(); }}
                />
            )}
            {data.error && <Empty error>{data.error}</Empty>}
            {linked?.state === 'missing' && <Note tone="live">{groupId ? `That contract isn’t one of ${spaceName}’s, or it isn’t one you can see.` : 'That contract isn’t one of yours: nothing you have shared has that id.'}</Note>}
            {linked?.state === 'error' && <Note tone="live">{linked.message || 'That contract could not be read.'}</Note>}
            {data.loaded && (<>
                <Faceted id="realms-profiles" rail={f.rail}>
                <DeskTable top={0} between={40}>
                    {/* whose contracts, said every time: yours, or the group's by name */}
                    {!sets.length && <ElementTable title={`${w.Your} contracts`} count={0} empty={empty} />}
                    {sets.map((s) => (
                        <ElementTable key={s.k} title={listTitle(s.k, w)} count={s.list.length}
                            note={s.k === 'member' ? `what each can do in ${spaceName}` : undefined}>
                            {s.list.map(cell)}
                        </ElementTable>
                    ))}
                </DeskTable>
                </Faceted>
            </>)}
        </NucleusPage>
    );
}
